import { LuFlame } from "react-icons/lu";
import { subjectName } from "../../shared/constants";
import { Avatar } from "./Avatar";
import { useApp } from "../store";
import { useEffect } from "react";

export type Scene = "arena" | "sea" | "land" | "race";
export const SCENE: Record<string, Scene> = { slagskibe: "sea", skattejagt: "land", taarn: "land", speedrun: "race", videnraes: "race", ludo: "arena", yatzy: "arena" };
export const MOTTO: Record<string, string> = {
  monopoly: "Køb · Byg · Forhandl · Bliv rigest",
  kryds: "Svar rigtigt · Placér dit symbol · Få tre på stribe",
  fire: "Svar rigtigt · Placér din brik · Få fire på stribe",
  skak: "Forudse træk · Find den bedste løsning · Bliv stærkere",
  slagskibe: "Svar rigtigt · Sigt · Sænk hele flåden",
  kortkamp: "Læg dit kort · Sig EDU! · Bliv først af med alle kort",
  quizduel: "Hurtige svar giver flest point",
  speedrun: "Hurtigere svar · Stærkere viden",
  videnraes: "Svar dig frem til målstregen",
  skattejagt: "Åbn portene · Find nøglerne · Tag skatten",
  taarn: "Svar rigtigt for guld · Byg · Hold basen",
  ludo: "Svar rigtigt · Slå terningen · Kom først hjem",
  yatzy: "Svar rigtigt · Hold terningerne · Fyld pointarket",
};

/** Sets the full-page scene backdrop for the current game. */
export function useScene(game: string | undefined) {
  useEffect(() => {
    document.body.dataset.scene = SCENE[game ?? ""] ?? "arena";
    return () => { delete document.body.dataset.scene; };
  }, [game]);
}

export function PlayerCard({ p, view, side }: { p: any; view: any; side: "left" | "right" }) {
  const { me } = useApp();
  const st = view.state;
  const mineSeat = p.seat === view.you;
  const turn = st && st.kind === "turn" && st.turn === p.seat && st.phase !== "over";
  const streak = mineSeat ? (st?.streak ?? st?.stats?.[p.seat]?.streak ?? null) : (st?.stats?.[p.seat]?.streak ?? null);
  const best = st?.stats?.[p.seat]?.best;
  const lp = mineSeat ? me?.levelProgress : null;
  return (
    <div className={`plcard ${side} ${turn ? "turn" : ""}`} aria-current={turn ? "true" : undefined}>
      <div className="ava"><Avatar equipped={p.avatar} size={56} label="" /></div>
      <div className="info">
        <div className="top">
          <span>{p.username}{mineSeat ? " (dig)" : ""}</span>
          {(streak ?? best) ? <span className="streak"><LuFlame size={14} aria-hidden="true" /> {streak ?? best} i streg</span> : null}
        </div>
        {lp ? (
          <>
            <div className="xpbar" role="progressbar" aria-label="XP" aria-valuemin={0} aria-valuemax={lp.needed} aria-valuenow={lp.into}><i style={{ width: `${Math.min(100, (lp.into / Math.max(1, lp.needed)) * 100)}%` }} /></div>
            <small className="muted">{lp.into} / {lp.needed} XP</small>
          </>
        ) : (
          <small className="muted">{p.isBot ? "Computer" : p.left ? "forladt" : !p.connected && view.status === "active" ? "forbindelse mistet" : p.title ?? "Spiller"}</small>
        )}
      </div>
      <span className={`hex ${side === "right" ? "pink" : ""}`} aria-label={`Niveau ${mineSeat ? me?.level : p.level ?? "?"}`}>Lv.{mineSeat ? me?.level : p.level ?? "?"}</span>
    </div>
  );
}

export function Plate({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="plate">
      <div className="banner-title"><b>{title}</b></div>
      <span className="sub">{sub}</span>
    </div>
  );
}

export function Ring({ value, max, big, label }: { value: number; max: number; big: string | number; label: string }) {
  const r = 52, c = 2 * Math.PI * r;
  const f = Math.max(0, Math.min(1, max ? value / max : 0));
  return (
    <div className="ringwrap">
      <div className="ring" role="timer" aria-label={`${big} ${label}`}>
        <svg viewBox="0 0 124 124"><circle className="bg" cx="62" cy="62" r={r} fill="none" strokeWidth="9" /><circle className="fg" cx="62" cy="62" r={r} fill="none" strokeWidth="9" strokeDasharray={c} strokeDashoffset={c * (1 - f)} /></svg>
        <div><b>{big}</b><small>{label}</small></div>
      </div>
    </div>
  );
}

export const subtitleFor = (view: any) => `${view.config.subjects.map((s: string) => subjectName(s)).join(" · ")} · ${view.config.grade}. klasse`;
